import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';

export type BAIdentityRow = {
  id: string;
  org_id: string | null;
  status: string | null;
  created_at: string | null;
};

export type BAIdentityData = {
  baRows: BAIdentityRow[];
  baIds: string[];
  primaryBaId: string | null;
  primaryBa: BAIdentityRow | null;
  hasMultipleBaRows: boolean;
};

type UseBaIdentityOptions = {
  enabled?: boolean;
};

const EMPTY_BA_IDENTITY: BAIdentityData = {
  baRows: [],
  baIds: [],
  primaryBaId: null,
  primaryBa: null,
  hasMultipleBaRows: false,
};

const isMissingColumnError = (error: any) => {
  const code = String(error?.code || '').toUpperCase();
  const message = String(error?.message || '').toLowerCase();
  return code === '42703' || message.includes('column') && message.includes('does not exist');
};

export const useBaIdentity = (options?: UseBaIdentityOptions) => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['ba-identity', user?.id],
    queryFn: async (): Promise<BAIdentityData> => {
      if (!user) return EMPTY_BA_IDENTITY;

      const { data, error } = await supabase
        .from('bas')
        .select('id, org_id, status, created_at')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
      let baRows = ((data || []) as BAIdentityRow[]).filter(
        (row) => typeof row.id === 'string' && row.id.length > 0,
      );

      if (error) {
        if (!isMissingColumnError(error)) throw error;

        // Schema-compat fallback for environments where optional columns are absent.
        const { data: fallbackRows, error: fallbackError } = await supabase
          .from('bas')
          .select('id')
          .eq('user_id', user.id)
          .order('id', { ascending: false });
        if (fallbackError) throw fallbackError;

        baRows = ((fallbackRows || []) as { id: string }[])
          .filter((row) => typeof row.id === 'string' && row.id.length > 0)
          .map((row) => ({
            id: row.id,
            org_id: null,
            status: null,
            created_at: null,
          }));
      }

      const baIds = baRows.map((row) => row.id);
      const primaryBa = baRows[0] ?? null;

      return {
        baRows,
        baIds,
        primaryBaId: primaryBa?.id ?? null,
        primaryBa,
        hasMultipleBaRows: baRows.length > 1,
      };
    },
    enabled: (options?.enabled ?? true) && !!user,
    staleTime: 1000 * 60 * 10,
  });
};
